import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { StrictMode, Suspense } from 'react';
import * as ReactDOM from 'react-dom/client';
import { AuthProvider } from '@ais-ui/auth';
import { ToasterWithViewState } from '@ais-ui/ui-core';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import App from './app/app';

import {
  LocalizationProvider,
  useLocalization,
} from '@ais-ui/locale-management';
import { translations } from './locales/translations';
import { ConfigContextProvider } from './configuration/configContext';
import { ErrorBoundary } from '@ais-ui/error-boundaries';
import { ProfileProvider } from '@ais-ui/profile';
import { useFavicon } from './configuration/useFavicon';
import { useSentry } from './configuration/useSentry';
import { useApplicationTitle } from './configuration/useApplicationTitle';
import { useTheme } from './configuration/useTheme';
import { useServiceWorker } from './configuration/useServiceWorker';

// Lazy load PopupApp - popups are separate windows that don't need the full bundle
const PopupApp = React.lazy(() =>
  import('./modules/Popup/PopupApp').then((m) => ({ default: m.PopupApp }))
);

// Minimal loading state for popup - must be outside lazy chunk
const PopupLoadingState = () => (
  <div className="flex items-center justify-center h-screen bg-background">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

class BuggyComponent extends React.Component {
  componentDidMount() {
    // Simulate a JS error
    throw new Error('I crashed!');
  }

  render() {
    // This will not be rendered
    return <h1>Hello World</h1>;
  }
}

function RootComponent() {
  return (
    // <StrictMode>
    <ConfigContextProvider>
      <LocalizationProvider translations={translations}>
        <AppComponent />
      </LocalizationProvider>
    </ConfigContextProvider>
    // </StrictMode>
  );
}

/**
 * Router that handles popup routes separately from main app
 * Popup routes bypass AuthProvider to avoid auto-login redirects
 */
const AppRouter = () => {
  const location = useLocation();
  const { messages } = useLocalization();
  
  // Handle service worker updates on navigation
  useServiceWorker();
  
  // Check if this is a popup route - these bypass AuthProvider
  const isPopupRoute = location.pathname.startsWith('/popup');
  
  if (isPopupRoute) {
    return (
      <ErrorBoundary messages={messages}>
        <Suspense fallback={<PopupLoadingState />}>
          <PopupApp />
        </Suspense>
      </ErrorBoundary>
    );
  }
  
  // Main app with full AuthProvider
  return (
    <ProfileProvider>
      <AuthProvider>
        <ToasterWithViewState>
          <ErrorBoundary messages={messages}>
            <App />
          </ErrorBoundary>
        </ToasterWithViewState>
      </AuthProvider>
    </ProfileProvider>
  );
};

const AppComponent = () => {
  const { messages } = useLocalization();
  useSentry();
  const { isLoading: isLoadingFavIcon } = useFavicon();
  const { isLoading: isLoadingTitle } = useApplicationTitle();
  const { isLoading: isLoadingTheme } = useTheme();

  if (isLoadingFavIcon || isLoadingTitle || isLoadingTheme) {
    return null;
  }

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <AppRouter />
      </QueryClientProvider>
    </Router>
  );
};

const queryClient = new QueryClient();
const rootElement = document.getElementById('root') as HTMLElement;
rootElement.className = 'isolate';
let root;

if (!root) {
  root = ReactDOM.createRoot(rootElement);
}

root.render(<RootComponent />);
